import {NgModule} from "@angular/core";
import {Router, RouterModule, Routes} from "@angular/router";
import {AppAuthGuard} from "./core/guards/app.authguard";
import {BackNavigationGuard} from "./core/guards/back-navigation.guard";
import {MyProfilesRedirectGuard} from "./core/guards/my-profiles-redirect.guard";
import {HomeScreenComponent} from "./feature/homescreen/homescreen.component";
import {EmployeeProfilemapComponent} from "./feature/employee-operations/employee-profilemap/employee-profilemap.component";
import {ChatBotComponent} from "./feature/chatbot/chat-bot.component";
import {InstanceWideSearchComponent} from "./feature/instance-wide-search/components/instance-wide-search/instance-wide-search.component";
import {SearchWrapperComponent} from "./feature/search-wrapper/search-wrapper.component";
import {PageNotFoundComponent} from "./feature/page-not-found/page-not-found.component";
import {PendingChangesGuard} from "./core/guards/pending-changes.guard";

const EMPLOYEE_EDIT = "employee-edit";
const EMPLOYEE_ID = "/:employeeId";
const PROJECT_DETAIL = "project-details";
const DOCUMENTTITLE_EMPLOYEE_EDIT = "mep.document-titles.employee-edit";
const DOCUMENTTITLE_PROJECT_EDIT = "mep.document-titles.project-edit";

const routes: Routes = [
	{
		path: "",
		redirectTo: "/homescreen",
		pathMatch: "full",
		data: {title: "mep.document-titles.homescreen"},
	},
	{
		path: "login",
		redirectTo: "/homescreen",
	},
	{
		path: "employee-list",
		loadChildren: () => import("./feature/employee-list/employee-list.module").then((m) => m.EmployeeListModule),
		canActivate: [AppAuthGuard, BackNavigationGuard],
		canDeactivate: [BackNavigationGuard],
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.employee-list",
			name: "EmployeeListComponent",
		},
	},
	{
		path: EMPLOYEE_EDIT,
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: "/employee-list",
			title: DOCUMENTTITLE_EMPLOYEE_EDIT,
		},
		children: [
			{
				path: ":employeeId",
				loadChildren: () =>
					import("./feature/employee-operations/employee-operations.module").then((m) => m.EmployeeOperationsModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					name: "EmployeeOperationsComponent",
				},
			},
			{
				path: ":employeeId/actionPlan",
				loadChildren: () => import("./feature/action-plan/action-plan.module").then((m) => m.ActionPlanModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					title: "mep.document-titles.action-plan",
					name: "ActionPlanComponent",
				},
			},
			{
				path: "absence-list/:employeeId",
				loadChildren: () => import("./feature/absence-list/absence-list.module").then((m) => m.AbsenceListModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: "/absence-list",
					title: "mep.document-titles.absence-list",
				},
			},
		],
	},
	{
		path: "skill-search",
		children: [
			{
				path: "",
				loadChildren: () => import("./feature/skill-search/skill-search.module").then((m) => m.SkillSearchModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: "/skill-search",
					title: "mep.document-titles.skill-search",
				},
			},
		],
	},
	{
		path: "admin-homescreen",
		loadChildren: () =>
			import("./feature/admin-homescreen/admin-homescreen.module").then((m) => m.AdminHomescreenModule),
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: "/admin-homescreen",
			title: "mep.document-titles.admin-homescreen",
		},
	},
	{
		path: "resource-request-list",
		loadChildren: () =>
			import("./feature/resource-request-list/resource-request-list.module").then((m) => m.ResourceRequestListModule),
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.resource-request-list",
		},
	},
	{
		path: "reports-screen",
		children: [
			{
				path: "",
				loadChildren: () => import("./feature/reports-screen/reports-screen.module").then((m) => m.ReportsScreenModule),
				canActivate: [AppAuthGuard],
			},
		],
		data: {
			title: "mep.document-titles.reports",
			breadcrumbs: "/reports-screen",
		},
	},
	{
		path: "absence-list",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.absence-list",
		},
		children: [
			{
				path: "",
				loadChildren: () => import("./feature/absence-list/absence-list.module").then((m) => m.AbsenceListModule),
				canActivate: [AppAuthGuard],
			},
			{
				path: ":employeeId",
				loadChildren: () => import("./feature/absence-list/absence-list.module").then((m) => m.AbsenceListModule),
				canActivate: [AppAuthGuard],
			},
			{
				path: EMPLOYEE_EDIT + EMPLOYEE_ID,
				loadChildren: () =>
					import("./feature/employee-operations/employee-operations.module").then((m) => m.EmployeeOperationsModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					title: DOCUMENTTITLE_EMPLOYEE_EDIT,
				},
			},
		],
	},
	{
		path: "my-projects",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.my-projects",
		},
		children: [
			{
				path: "",
				loadChildren: () => import("./feature/my-projects/my-projects.module").then((m) => m.MyProjectsModule),
				canActivate: [AppAuthGuard],
			},
			{
				path: "project-edit/:id",
				loadChildren: () => import("./feature/project-edit/project-edit.module").then((m) => m.ProjectEditModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: PROJECT_DETAIL,
					title: DOCUMENTTITLE_PROJECT_EDIT,
					name: "ProjectEditComponent",
				},
			},
			{
				path: EMPLOYEE_EDIT + EMPLOYEE_ID,
				loadChildren: () =>
					import("./feature/employee-operations/employee-operations.module").then((m) => m.EmployeeOperationsModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					title: DOCUMENTTITLE_EMPLOYEE_EDIT,
					name: "EmployeeOperationsComponent",
				},
			},
		],
	},
	{
		path: "project-list",
		loadChildren: () => import("./feature/project-list/project-list.module").then((m) => m.ProjectListModule),
		canActivate: [AppAuthGuard, BackNavigationGuard],
		canDeactivate: [BackNavigationGuard],
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.project-list",
			name: "ProjectListComponent",
		},
	},
	{
		path: "action-plan-list",
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.action-plan-list",
		},
		loadChildren: () => import("./feature/action-plan/action-plan.module").then((m) => m.ActionPlanModule),
	},
	{
		path: "project-edit",
		data: {
			breadcrumbs: "/project-list",
			title: DOCUMENTTITLE_PROJECT_EDIT,
		},
		children: [
			{
				path: ":id",
				loadChildren: () => import("./feature/project-edit/project-edit.module").then((m) => m.ProjectEditModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: PROJECT_DETAIL,
				},
			},
		],
	},
	{
		path: "homescreen",
		component: HomeScreenComponent,
		canActivate: [AppAuthGuard],
		data: {
			title: "mep.document-titles.homescreen",
		},
	},
	{
		path: "employee-capacities",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.employee-capacities",
		},
		children: [
			{
				path: "",
				loadChildren: () =>
					import("./feature/employee-capacity-container/employee-capacity-container.module").then(
						(m) => m.EmployeeCapacityContainerModule,
					),
				canActivate: [AppAuthGuard, BackNavigationGuard],
				canDeactivate: [BackNavigationGuard],
				data: {
					name: "EmployeeCapacityContainerComponent",
				},
			},
			{
				path: EMPLOYEE_EDIT + EMPLOYEE_ID,
				loadChildren: () =>
					import("./feature/employee-operations/employee-operations.module").then((m) => m.EmployeeOperationsModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					title: DOCUMENTTITLE_EMPLOYEE_EDIT,
					name: "EmployeeEditComponent",
				},
			},
		],
	},
	{
		path: "instance-wide-search",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.instance-wide-search",
		},
		children: [
			{
				path: "",
				loadChildren: () =>
					import("./feature/instance-wide-search/instance-wide-search.module").then((m) => m.InstanceWideSearchModule),
				canActivate: [AppAuthGuard, BackNavigationGuard],
				canDeactivate: [BackNavigationGuard, PendingChangesGuard],
				component: InstanceWideSearchComponent,
			},
		],
	},
	{
		path: "project-total-capacities",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.project-total-capacities",
		},
		children: [
			{
				path: "",
				loadChildren: () =>
					import("./feature/project-total-capacity-list/project-total-capacities.module").then(
						(m) => m.ProjectTotalCapacitiesModule,
					),
				canActivate: [AppAuthGuard],
			},
			{
				path: "project-edit/:id",
				loadChildren: () => import("./feature/project-edit/project-edit.module").then((m) => m.ProjectEditModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: PROJECT_DETAIL,
					title: DOCUMENTTITLE_PROJECT_EDIT,
				},
			},
		],
	},
	{
		path: "global-search",
		data: {
			breadcrumbs: true,
		},
		children: [
			{
				path: "",
				loadChildren: () =>
					import("./feature/global-search-list/global-search-list.module").then((m) => m.GlobalSearchListModule),
				canActivate: [AppAuthGuard],
			},
			{
				path: "project-edit/:id",
				loadChildren: () => import("./feature/project-edit/project-edit.module").then((m) => m.ProjectEditModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: PROJECT_DETAIL,
					title: DOCUMENTTITLE_PROJECT_EDIT,
				},
			},
			{
				path: EMPLOYEE_EDIT + EMPLOYEE_ID,
				loadChildren: () =>
					import("./feature/employee-operations/employee-operations.module").then((m) => m.EmployeeOperationsModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: EMPLOYEE_EDIT,
					title: DOCUMENTTITLE_EMPLOYEE_EDIT,
				},
			},
		],
	},
	{
		path: "profilemap-iframe",
		data: {
			breadcrumbs: true,
			title: "mep.document-titles.profilemap",
		},
		children: [
			{
				path: "",
				loadChildren: () =>
					import("./feature/profilemap-iframe/profilemap-iframe.module").then((m) => m.ProfilemapIframeModule),
				canActivate: [AppAuthGuard],
			},
		],
	},
	{
		path: "profilemap/:persNr",
		component: EmployeeProfilemapComponent,
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: "/profilemap",
		},
	},
	{
		path: "chatbot",
		component: ChatBotComponent,
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: "/chatbot",
		},
	},
	{
		path: "search",
		component: SearchWrapperComponent,
		canActivate: [AppAuthGuard],
		children: [{path: "**", component: SearchWrapperComponent, canActivate: [AppAuthGuard], pathMatch: "full"}],
	},
	{
		path: "my-profiles",
		canActivate: [AppAuthGuard],
		data: {
			breadcrumbs: "/my-profiles",
			title: "mep.document-titles.my-profiles",
		},
		children: [
			{
				path: "",
				pathMatch: "full",
				canActivate: [MyProfilesRedirectGuard],
				component: HomeScreenComponent,
			},
			{
				path: ":employeeId",
				loadChildren: () => import("./feature/my-profiles/my-profiles.module").then((m) => m.MyProfilesModule),
				canActivate: [AppAuthGuard],
				data: {
					breadcrumbs: "my-profiles",
					name: "MyProfilesComponent",
				},
			},
		],
	},
	{
		path: "**",
		component: PageNotFoundComponent,
		canActivate: [AppAuthGuard],
	},
];

// eslint-disable-next-line @typescript-eslint/naming-convention
export function BackNavigationGuardFactory(router: Router) {
	// This configuration only works if the component has the BackNavigationGuard assigned to its 'canActivate' and 'canDeactivate'
	return new BackNavigationGuard(router, [
		{
			componentName: "EmployeeListComponent",
			validTargets: ["EmployeeOperationsComponent", "ActionPlanComponent"],
		},
		{
			componentName: "ProjectListComponent",
			validTargets: ["ProjectEditComponent"],
		},
		{
			componentName: "ActionPlanListComponent",
			validTargets: ["ActionPlanComponent"],
		},
		{
			componentName: "EmployeeCapacityContainerComponent",
			validTargets: ["EmployeeOperationsComponent", "ActionPlanComponent"],
		},
	]);
}

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule],
	providers: [{provide: BackNavigationGuard, useFactory: BackNavigationGuardFactory, deps: [Router]}],
})
export class AppRoutingModule {}
